import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AdminHub } from "./components/admin/AdminHub";
import { LoginScreen } from "./components/auth/LoginScreen";
import { CashierGrid } from "./components/cashier/CashierGrid";
import { FailedReceiptsBanner } from "./components/layout/FailedReceiptsBanner";
import { Header } from "./components/layout/Header";
import { Sidebar } from "./components/layout/Sidebar";
import { ToastHost } from "./components/layout/ToastHost";
import { CatalogPanel } from "./components/catalog/CatalogPanel";
import { CustomersPanel } from "./components/customers/CustomersPanel";
import { ReceiptsPanel } from "./components/receipts/ReceiptsPanel";
import { ShiftControlPanel } from "./components/shifts/ShiftControlPanel";
import { EInvoicePanel } from "./components/einvoice/EInvoicePanel";
import { AuditPanel } from "./components/audit/AuditPanel";
import { ExpensesPanel } from "./components/expenses/ExpensesPanel";
import { ReportsPanel } from "./components/reports/ReportsPanel";
import { SetupHub } from "./components/setup/SetupHub";
import { StaffPanel } from "./components/staff/StaffPanel";
import { DownloadsPanel } from "./components/downloads/DownloadsPanel";
import { HardwarePanel } from "./components/hardware/HardwarePanel";
import { HrHub } from "./components/hr/HrHub";
import { LoyaltyPanel } from "./components/loyalty/LoyaltyPanel";
import { PartiesPanel } from "./components/parties/PartiesPanel";
import { BranchPicker } from "./components/v2/BranchPicker";
import { WalletPanel } from "./components/v2/WalletPanel";
import { WhatsAppPanel } from "./components/whatsapp/WhatsAppPanel";
import { useAppState } from "./context/AppStateContext";
import { useAuth } from "./context/AuthContext";
import { isV2 } from "./lib/config";
import { startBarcodeCapture } from "./lib/hardware";
import { startHardwareMonitor } from "./lib/hardwareStore";
import { startHeartbeat } from "./lib/heartbeat";

function Workspace() {
  const { tab, setTab } = useAppState();
  const { can } = useAuth();

  // a person who may not open the page they are on (permissions changed, or the first page is not theirs) lands on the first allowed one
  const PAGE_PERM: Record<string, Parameters<typeof can>[0] | undefined> = { cashier: "pos", whatsapp: "whatsapp", parties: "parties", loyalty: "loyalty", customers: "customers", reports: "reports", expenses: "expenses", receipts: "receipts", shifts: "shifts", einvoice: "einvoice", audit: "audit", wallet: "wallet", hardware: "hardware", downloads: "downloads", pricing: "pricing", admin: "admin", staff: "staff", setup: "setup" };
  useEffect(() => {
    const need = PAGE_PERM[tab];
    if (need && !can(need)) {
      const first = Object.entries(PAGE_PERM).find(([, p]) => p && can(p));
      if (first) setTab(first[0] as typeof tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, can]);

  // Hardware barcode scanner (native bridge in Electron, keyboard-wedge in the browser).
  useEffect(() => {
    return startBarcodeCapture();
  }, []);
  // The printer's state (Electron) and a heartbeat to the server so managers can see every terminal
  useEffect(() => {
    const stopMonitor = startHardwareMonitor();
    const stopBeat = isV2() ? startHeartbeat() : () => undefined;
    return () => { stopMonitor(); stopBeat(); };
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <FailedReceiptsBanner />
      <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6" id="main">
          {tab === "cashier" && can("pos") && <CashierGrid />}
          {tab === "whatsapp" && can("whatsapp") && <WhatsAppPanel />}
          {tab === "parties" && can("parties") && <PartiesPanel />}
          {tab === "loyalty" && can("loyalty") && <LoyaltyPanel />}
          {tab === "customers" && isV2() && can("customers") && <CustomersPanel />}
          {tab === "reports" && isV2() && can("reports") && <ReportsPanel />}
          {tab === "expenses" && isV2() && can("expenses") && <ExpensesPanel />}
          {tab === "receipts" && isV2() && can("receipts") && <ReceiptsPanel />}
          {tab === "shifts" && isV2() && can("shifts") && <ShiftControlPanel />}
          {tab === "einvoice" && isV2() && can("einvoice") && <EInvoicePanel />}
          {tab === "audit" && isV2() && can("audit") && <AuditPanel />}
          {tab === "wallet" && can("wallet") && <WalletPanel />}
          {tab === "hr" && isV2() && <HrHub />}
          {tab === "hardware" && isV2() && can("hardware") && <HardwarePanel />}
          {tab === "pricing" && isV2() && can("pricing") && <CatalogPanel />}
          {tab === "staff" && isV2() && can("staff") && <StaffPanel />}
          {tab === "setup" && isV2() && can("setup") && <SetupHub />}
          {tab === "downloads" && isV2() && can("downloads") && <DownloadsPanel />}
          {tab === "admin" && can("admin") && <AdminHub />}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const { t } = useTranslation();
  const { user, booting, needsBranch } = useAuth();
  if (booting) return <div className="flex min-h-screen items-center justify-center text-sm font-bold text-gray-500">{t("common.loading")}</div>;
  return (
    <>
      {user ? needsBranch ? <BranchPicker /> : <Workspace /> : <LoginScreen />}
      <ToastHost />
    </>
  );
}
